import { format, parseISO } from 'date-fns';
import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import sha1 from 'sha1';
import { UserContext } from '../../../../context';
import { StoryCard, StoryCardQuoteContent } from '../../../../services';
import { useAnalytics } from '../../../hooks';
import { getCssVar } from '../../../utils';
import { AuthorInfo } from '../../AuthorInfo';
import { CardOpenButton } from '../../CardOpenButton';
import { IconLabel } from '../../IconLabel';
import { ImageGallery, ImageGalleryItem } from '../../ImageGallery';
import { PinnedIcon } from '../../PinnedIcon';
import { UserMentionPreview } from '../../UserMentionPreview';
import { FeedCardMargin } from '../FeedCardMargin';

import classNames from 'classnames';
import classes from './FeedCardSocialCommon.module.scss';

interface FeedCardSocialCommonProps {
  card: StoryCard;
}

export const FeedCardSocialCommon: FunctionComponent<FeedCardSocialCommonProps> = memo(
  ({ card }) => {
    const { logClickOpenArticle } = useAnalytics();

    const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

    const {
      id: cardId,
      storyId,
      options,
      content,
      isPinnedToTopOfNewsFeed,
      isPinnedToTopOfStory,
      mentionedUsers,
    } = card;

    const { allowDisplayAuthor } = options;

    const {
      url,
      headline,
      quote,
      gallery,
      quoteSource,
      quotePerson,
      quotePersonImage,
      quotePersonHandle,
      quoteCreated,
    } = content as StoryCardQuoteContent;

    const imageGalleryItems: ImageGalleryItem[] = gallery.map(({ image }) => {
      return { image };
    });

    const headerSubTitle = useMemo(
      () =>
        `@${quotePersonHandle} via ${quoteSource?.toLowerCase()} • ${format(
          parseISO(quoteCreated ?? ''),
          'dd MMM'
        )}`,
      [quoteCreated, quotePersonHandle, quoteSource]
    );

    const logClickOpen = useCallback(() => {
      logClickOpenArticle({
        user_Id: sha1(`${userId}`),
        channel_id: channelId,
        story_id: storyId,
        item_id: cardId,
        time_stamp: new Date().toISOString(),
      });
    }, [cardId, channelId, logClickOpenArticle, storyId, userId]);

    const cardHeaderClickHandler = useCallback(() => {
      logClickOpen();
      window.open(url, '_blank', 'noopener, noreferrer');
    }, [logClickOpen, url]);

    const isFramed = useMemo(() => allowDisplayAuthor || headline, [allowDisplayAuthor, headline]);

    const authorInfoIcon = useMemo(
      () => (
        <IconLabel
          className={classes['icon']}
          iconId={'arrow-rounded-right-up'}
          iconSize={20}
          color={getCssVar('--card-social-icon-color')}
          singleColor
        />
      ),
      []
    );

    return (
      <FeedCardMargin top left right>
        <UserMentionPreview
          mentionedUsers={mentionedUsers}
          className={classes['mentions']}
          openProfile
        />

        <div className={classNames({ [classes['wrapper']]: isFramed })}>
          <div
            className={classNames(classes['header-wrapper'], {
              [classes['header-wrapper--inner']]: isFramed,
            })}
            onClick={cardHeaderClickHandler}
          >
            <CardOpenButton label={quotePerson} />

            <FeedCardMargin bottom>
              <div className={classes['author-info']}>
                <PinnedIcon
                  isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
                  isPinnedToTopOfStory={isPinnedToTopOfStory}
                  color={getCssVar('--base-link-text-color')}
                  showSeparator
                />
                <AuthorInfo
                  avatarUrl={quotePersonImage?.url}
                  title={quotePerson}
                  subTitle={headerSubTitle}
                  icon={authorInfoIcon}
                />
              </div>
            </FeedCardMargin>
            {quote && <div className={classes['quote']}>{quote}</div>}
          </div>

          <ImageGallery
            items={imageGalleryItems}
            modalPreviewOnClick
            onLogClickOpenArticle={logClickOpen}
          />
        </div>
      </FeedCardMargin>
    );
  }
);
